import { useState, useCallback } from 'react';
import useApi from './useApi';

/**
 * Custom hook for file upload operations
 * @param {Object} options - Upload configuration options
 * @returns {Object} File upload state and methods
 */
const useFileUpload = (options = {}) => {
  const {
    endpoint = '/api/upload',
    maxSize = 10 * 1024 * 1024, // 10MB default
    maxFiles = 5,
    acceptedTypes = [],
    onSuccess,
    onError
  } = options;

  const [files, setFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({});
  const [errors, setErrors] = useState({});
  const { loading, error, execute } = useApi();

  const validateFile = useCallback((file) => {
    const errors = [];

    // Check file size
    if (file.size > maxSize) {
      errors.push(`File size must be less than ${formatFileSize(maxSize)}`);
    }

    // Check file type
    if (acceptedTypes.length > 0 && !acceptedTypes.includes(file.type)) {
      errors.push(`File type ${file.type} is not supported`);
    }

    return errors;
  }, [maxSize, acceptedTypes]);

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const addFiles = useCallback((newFiles) => {
    const validFiles = [];
    const fileErrors = {};

    newFiles.forEach((file, index) => {
      const validationErrors = validateFile(file);
      
      if (validationErrors.length > 0) {
        fileErrors[file.name] = validationErrors;
      } else {
        validFiles.push({
          file,
          id: Date.now() + index,
          name: file.name,
          size: file.size,
          type: file.type,
          status: 'pending'
        });
      }
    });

    if (Object.keys(fileErrors).length > 0) {
      setErrors(prev => ({ ...prev, ...fileErrors }));
    }

    if (validFiles.length > 0) {
      setFiles(prev => {
        const totalFiles = prev.length + validFiles.length;
        if (totalFiles > maxFiles) {
          const allowedFiles = validFiles.slice(0, maxFiles - prev.length);
          setErrors(prev => ({
            ...prev,
            maxFiles: `Maximum ${maxFiles} files allowed`
          }));
          return [...prev, ...allowedFiles];
        }
        return [...prev, ...validFiles];
      });
    }
  }, [validateFile, maxFiles]);

  const removeFile = useCallback((fileId) => {
    setFiles(prev => prev.filter(f => f.id !== fileId));
    setUploadProgress(prev => {
      const newProgress = { ...prev };
      delete newProgress[fileId];
      return newProgress;
    });
    setErrors(prev => {
      const newErrors = { ...prev };
      const fileName = files.find(f => f.id === fileId)?.name;
      if (fileName) {
        delete newErrors[fileName];
      }
      return newErrors;
    });
  }, [files]);

  const uploadFile = useCallback(async (fileData, additionalData = {}) => {
    const formData = new FormData();
    formData.append('file', fileData.file);
    
    // Add additional data
    Object.keys(additionalData).forEach(key => {
      formData.append(key, additionalData[key]);
    });

    try {
      // Update file status
      setFiles(prev => prev.map(f => 
        f.id === fileData.id ? { ...f, status: 'uploading' } : f
      ));

      const response = await execute(() => {
        return fetch(endpoint, {
          method: 'POST',
          body: formData,
          headers: {
            // Don't set Content-Type, let browser set it with boundary
          }
        });
      });

      if (!response.ok) {
        throw new Error(`Upload failed: ${response.statusText}`);
      }

      const result = await response.json();

      // Update file status and add server response
      setFiles(prev => prev.map(f => 
        f.id === fileData.id 
          ? { ...f, status: 'completed', url: result.url, serverId: result.id }
          : f
      ));

      if (onSuccess) {
        onSuccess(result, fileData);
      }

      return result;
    } catch (err) {
      // Update file status
      setFiles(prev => prev.map(f => 
        f.id === fileData.id ? { ...f, status: 'error', error: err.message } : f
      ));

      if (onError) {
        onError(err, fileData);
      }

      throw err;
    }
  }, [endpoint, execute, onSuccess, onError]);

  const uploadFiles = useCallback(async (additionalData = {}) => {
    const pendingFiles = files.filter(f => f.status === 'pending');
    const results = [];

    for (const file of pendingFiles) {
      try {
        const result = await uploadFile(file, additionalData);
        results.push(result);
      } catch (err) {
        console.error(`Failed to upload ${file.name}:`, err);
      }
    }

    return results;
  }, [files, uploadFile]);

  const uploadSingleFile = useCallback(async (file, additionalData = {}) => {
    const formData = new FormData();
    formData.append('file', file);
    
    // Add additional data
    Object.keys(additionalData).forEach(key => {
      formData.append(key, additionalData[key]);
    });

    try {
      const response = await execute(() => {
        return fetch(endpoint, {
          method: 'POST',
          body: formData
        });
      });

      if (!response.ok) {
        throw new Error(`Upload failed: ${response.statusText}`);
      }

      const result = await response.json();

      if (onSuccess) {
        onSuccess(result, { file });
      }

      return result;
    } catch (err) {
      if (onError) {
        onError(err, { file });
      }
      throw err;
    }
  }, [endpoint, execute, onSuccess, onError]);

  const clearFiles = useCallback(() => {
    setFiles([]);
    setUploadProgress({});
    setErrors({});
  }, []);

  const clearErrors = useCallback(() => {
    setErrors({});
  }, []);

  const getFilesByStatus = useCallback((status) => {
    return files.filter(f => f.status === status);
  }, [files]);

  const hasErrors = Object.keys(errors).length > 0;
  const hasFiles = files.length > 0;
  const pendingFiles = files.filter(f => f.status === 'pending');
  const uploadingFiles = files.filter(f => f.status === 'uploading');
  const completedFiles = files.filter(f => f.status === 'completed');
  const errorFiles = files.filter(f => f.status === 'error');

  return {
    // State
    files,
    uploadProgress,
    errors,
    loading,
    error,
    
    // Computed state
    hasErrors,
    hasFiles,
    pendingFiles,
    uploadingFiles,
    completedFiles,
    errorFiles,
    
    // Methods
    addFiles,
    removeFile,
    uploadFile,
    uploadFiles,
    uploadSingleFile,
    clearFiles,
    clearErrors,
    getFilesByStatus,
    
    // Utils
    formatFileSize,
    validateFile
  };
};

export default useFileUpload;